import * as React from "react"

import { cn } from "@/lib/utils"
import { FIELD_CHROME, FIELD_HEIGHT } from "@/components/ui/field-chrome"

type KeyboardProps = Pick<
  React.ComponentProps<"input">,
  "inputMode" | "autoComplete" | "autoCapitalize" | "autoCorrect" | "spellCheck" | "enterKeyHint"
>

/**
 * The keyboard an input gets when its caller says nothing. On iOS the type
 * alone does not pick a sensible keyboard: `type="number"` shows the full
 * QWERTY row with digits on top (no decimal pad), an email field
 * auto-capitalises the first letter and "corrects" the domain, and a search
 * field offers Return instead of Search. These are only DEFAULTS — any prop
 * the caller passes, such as
 * `inputMode="numeric"` for whole numbers, or `autoComplete="off"` on a field
 * describing someone other than the reader) always wins.
 */
export function keyboardDefaults(
  type: string | undefined,
  props: KeyboardProps = {},
): KeyboardProps {
  const d: KeyboardProps = {}
  switch (type) {
    case "number":
      d.inputMode = "decimal"
      break
    case "email":
      d.autoComplete = "email"
      d.autoCapitalize = "none"
      d.autoCorrect = "off"
      d.spellCheck = false
      break
    case "tel":
      d.autoComplete = "tel"
      d.inputMode = "tel"
      break
    case "url":
      d.autoCapitalize = "none"
      d.autoCorrect = "off"
      d.spellCheck = false
      break
    case "search":
      d.enterKeyHint = "search"
      break
  }
  for (const key of Object.keys(d) as (keyof KeyboardProps)[]) {
    if (props[key] !== undefined) delete d[key]
  }
  return d
}

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    // h-9 to match icon buttons and default buttons; min-h-touch on coarse
    // pointers so the field is comfortably tappable without changing desktop
    // density. h-9 stays as the base so existing side-by-side alignment with
    // buttons is preserved everywhere.
    return (
      <input
        type={type}
        {...keyboardDefaults(type, props)}
        className={cn(
          FIELD_CHROME,
          FIELD_HEIGHT,
          "flex file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          // Error state must not be colour-only — WCAG 2.2 SC 1.4.1 (Use of
          // Colour). The ring thickens as well as changes hue, so the state is
          // still perceivable without colour vision. Driven by aria-invalid so
          // the visual and the accessible state can never disagree.
          "aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-destructive",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
